// Color arithmetic on six-digit hex colors, shared by the template builder (placePalette) and the widget, which
// picks the text color of the rows it draws under template artwork against the card's resolved footer (#1039).

const HEX = /^#[0-9a-f]{6}$/i

export const MIN_TEXT_CONTRAST = 4.5

export function isHexColor(value) {
  return typeof value === 'string' && HEX.test(value.trim())
}

export function toRgb(hex) {
  return [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16))
}

export function toHex(rgb) {
  return `#${rgb.map((channel) => Math.round(Math.min(255, Math.max(0, channel))).toString(16).padStart(2, '0')).join('')}`
}

/** `share` of the way from one color to another: `mix(background, color, alpha)` is `color` painted at `alpha` over the background. */
export function mix(from, to, share) {
  const a = toRgb(from)
  const b = toRgb(to)
  return toHex(a.map((channel, index) => channel + (b[index] - channel) * share))
}

export function luminance(hex) {
  const [r, g, b] = toRgb(hex).map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio between two six-digit hex colors. */
export function contrastRatio(a, b) {
  const [lighter, darker] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (lighter + 0.05) / (darker + 0.05)
}

/** Black or white, whichever reads better on the background. */
export function readableOn(background) {
  return contrastRatio('#ffffff', background) >= contrastRatio('#111111', background) ? '#ffffff' : '#111111'
}

const SHORT_HEX = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
const LONG_HEX = /^#([0-9a-f]{6})(?:ff)?$/i
const CHANNEL = '(\\d{1,3})'
// rgb(r, g, b) or rgb(r g b), with an opaque alpha (1, 1.0, 100%) after a comma or a slash, in rgb() or rgba().
const RGB = new RegExp(`^rgba?\\(\\s*${CHANNEL}\\s*(?:,\\s*${CHANNEL}\\s*,\\s*${CHANNEL}|\\s+${CHANNEL}\\s+${CHANNEL})\\s*(?:[,/]\\s*(1(?:\\.0+)?|100%)\\s*)?\\)$`, 'i')

/**
 * The opaque color a CSS value names, as lowercase `#rrggbb`: `#rgb`, `#rrggbb`, `#rrggbbff` and opaque `rgb()` /
 * `rgba()`. Anything else (a variable, a gradient, color-mix, hsl, transparency) is null.
 */
export function toHex6(value) {
  if (typeof value !== 'string') return null
  const text = value.trim()
  const short = SHORT_HEX.exec(text)
  if (short) return `#${short.slice(1).map((digit) => digit + digit).join('')}`.toLowerCase()
  const long = LONG_HEX.exec(text)
  if (long) return `#${long[1]}`.toLowerCase()
  const rgb = RGB.exec(text)
  if (!rgb) return null
  const channels = (rgb[1] && rgb[2] ? [rgb[1], rgb[2], rgb[3]] : [rgb[1], rgb[4], rgb[5]]).map(Number)
  if (channels.some((channel) => channel > 255)) return null
  return toHex(channels)
}

/**
 * The text color for `background`, as `{ color, kept }`. `preferred`, as given, is kept when it reaches
 * MIN_TEXT_CONTRAST as painted: composited at `opacity` over the background. Otherwise the color is a substitute
 * meant to be painted plain (full opacity, no tint): white or #111111 (readableOn) when that reaches the ratio plain,
 * else pure black, which a mid grey needs; one of the three always does on an opaque background. `kept` tells the
 * caller whether to keep the preferred color's painting, even when the substitute is the same color. Undefined when
 * either is not an opaque color toHex6 reads.
 */
export function readableTextOn(background, preferred, { opacity = 1 } = {}) {
  const bg = toHex6(background)
  const text = toHex6(preferred)
  if (!bg || !text) return undefined
  if (contrastRatio(bg, mix(bg, text, opacity)) >= MIN_TEXT_CONTRAST) return { color: preferred, kept: true }
  const soft = readableOn(bg)
  return { color: contrastRatio(bg, soft) >= MIN_TEXT_CONTRAST ? soft : '#000000', kept: false }
}
